import {useCallback, useRef, useState} from "react";

import VLFTextField from "@/components/Input/TextField";
import VLFSelectNew from "@/components/Input/SelectNew";
import MainActions from "@/components/MainActions";

import CloseIcon from "@mui/icons-material/Close";

import {videoTagTypeOptions} from "@/utils/constants";

import "./MatchVideoTagging.scss";

export default function MatchVideoTagging({matchData}) {
	const [tempVideoUrl, setTempVideoUrl] = useState("");
	const [videoStartTime, setVideoStartTime] = useState(0);
	const [videoTags, setVideoTags] = useState([]);
	const [pendingTag, setPendingTag] = useState(null);
	const ytPlayerRef = useRef(null);
	const ytContainerRef = useRef(null);

	const initYTPlayer = useCallback((videoId) => {
		if (!videoId || !ytContainerRef.current) return;
		const createPlayer = (vid) => {
			if (ytPlayerRef.current) {
				try {
					ytPlayerRef.current.destroy();
				} catch {}
			}
			ytPlayerRef.current = new window.YT.Player(ytContainerRef.current, {
				videoId: vid,
				height: "360",
				width: "100%",
				playerVars: {rel: 0, modestbranding: 1},
			});
		};
		if (!window.YT) {
			const tag = document.createElement("script");
			tag.src = "https://www.youtube.com/iframe_api";
			document.head.appendChild(tag);
			window.onYouTubeIframeAPIReady = () => createPlayer(videoId);
		} else {
			createPlayer(videoId);
		}
	}, []);

	const activeUrl = matchData?.videoUrl || tempVideoUrl;
	const ytMatch = activeUrl?.match(
		/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/,
	);
	const vmMatch = activeUrl?.match(/vimeo\.com\/(\d+)/);
	const ytId = ytMatch ? ytMatch[1] : null;
	const vmId = vmMatch ? vmMatch[1] : null;
	const hasVideo = ytId || vmId;

	const fmtTime = (min, sec) =>
		`${String(min || 0).padStart(2, "0")}:${String(sec || 0).padStart(2, "0")}`;

	const TAG_TYPES = videoTagTypeOptions;
	const TAG_COLORS = {
		goal: "#16a34a",
		shot: "#2563eb",
		shot_on_target: "#1d4ed8",
		assist: "#7c3aed",
		dangerous_action: "#ea580c",
		dribbling: "#8b5cf6",
		cross: "#6366f1",
		save: "#059669",
		interception: "#0d9488",
		tackle: "#0891b2",
		clearance: "#0e7490",
		recovery: "#14b8a6",
		corner: "#d97706",
		free_kick: "#b45309",
		penalty: "#92400e",
		throw_in: "#a16207",
		foul: "#dc2626",
		yellow_card: "#eab308",
		red_card: "#dc2626",
		substitution: "#6366f1",
		offside: "#9333ea",
		positive_play: "#16a34a",
		mistake: "#ef4444",
		positioning: "#0891b2",
		movement: "#7c3aed",
		tactical: "#64748b",
		transition: "#475569",
		pressing: "#334155",
		other: "#94a3b8",
	};
	const TAG_GROUPS = [...new Set(TAG_TYPES.map((t) => t.group))];

	const getCurrentVideoTime = () => {
		try {
			const t = ytPlayerRef.current?.getCurrentTime?.() || 0;
			return {
				minute: Math.floor(t / 60),
				second: Math.floor(t % 60),
			};
		} catch {
			return {minute: 0, second: 0};
		}
	};

	const startPendingTag = (type) => {
		const label = TAG_TYPES.find((t) => t.value === type)?.label || type;
		const time = getCurrentVideoTime();
		setPendingTag({
			tagType: type,
			tagLabel: label,
			minute: String(time.minute),
			second: String(time.second),
			team: "",
			playerName: "",
			notes: "",
		});
	};

	const confirmPendingTag = () => {
		if (!pendingTag) return;
		setVideoTags([
			...videoTags,
			{
				...pendingTag,
				minute: parseInt(pendingTag.minute) || 0,
				second: parseInt(pendingTag.second) || 0,
			},
		]);
		setPendingTag(null);
	};

	const seekTo = (min, sec) => {
		const t = (min || 0) * 60 + (sec || 0);
		try {
			ytPlayerRef.current?.seekTo?.(t, true);
		} catch {}
		setVideoStartTime(t);
	};

	const homeTags = videoTags
		.filter((t) => t.team === "home")
		.sort(
			(a, b) =>
				(a.minute || 0) * 60 +
				(a.second || 0) -
				((b.minute || 0) * 60 + (b.second || 0)),
		);
	const awayTags = videoTags
		.filter((t) => t.team === "away")
		.sort(
			(a, b) =>
				(a.minute || 0) * 60 +
				(a.second || 0) -
				((b.minute || 0) * 60 + (b.second || 0)),
		);
	const neutralTags = videoTags
		.filter((t) => !t.team)
		.sort(
			(a, b) =>
				(a.minute || 0) * 60 +
				(a.second || 0) -
				((b.minute || 0) * 60 + (b.second || 0)),
		);

	const tagColor = (type) => TAG_COLORS[type] || TAG_COLORS.other;
	const teamOptions = [
		{value: "home", label: matchData?.homeClub || "Casa"},
		{value: "away", label: matchData?.awayClub || "Ospite"},
	];
	const updatePending = (field, value) =>
		setPendingTag({...pendingTag, [field]: value});

	const renderTagChip = (tag, i) => (
		<div
			key={i}
			className="mvt-chip"
			style={{"--tag-color": tagColor(tag.tagType)}}
			onClick={() => seekTo(tag.minute, tag.second)}
		>
			<span className="mvt-dot" />
			<span className="mvt-chip__time">{fmtTime(tag.minute, tag.second)}</span>
			<span className="mvt-chip__label">{tag.tagLabel || tag.tagType}</span>
			{tag.playerName && (
				<span className="mvt-chip__player">{tag.playerName}</span>
			)}
			{tag.notes && <span className="mvt-chip__notes">{tag.notes}</span>}
			<button
				type="button"
				className="mvt-chip__remove"
				aria-label="Rimuovi"
				onClick={(e) => {
					e.stopPropagation();
					setVideoTags(
						videoTags.filter((_, j) => videoTags.indexOf(tag) !== j),
					);
				}}
			>
				<CloseIcon sx={{fontSize: 14}} />
			</button>
		</div>
	);

	const renderColumn = (title, tags, prefix) => (
		<div className="mvt-column">
			<div className="mvt-column__title">
				{title} ({tags.length})
			</div>
			<div className="mvt-column__list">
				{tags.map((tag, i) => renderTagChip(tag, prefix + i))}
				{tags.length === 0 && <p className="mvt-empty">Nessun momento</p>}
			</div>
		</div>
	);

	return (
		<div className="col-sm-12 padding-small mt-2">
			{!matchData?.videoUrl && (
				<VLFTextField
					name="tempVideoUrl"
					value={tempVideoUrl}
					onChange={(e) => setTempVideoUrl(e.target.value)}
					label="Video della partita (YouTube o Vimeo)"
					placeholder="Incolla il link"
				/>
			)}

			{hasVideo && (
				<div className="mvt">
					<div className="mvt__workspace">
						<div className="mvt__video">
							{ytId ? (
								<div
									className="mvt__player"
									ref={(el) => {
										ytContainerRef.current = el;
										if (el && !ytPlayerRef.current) initYTPlayer(ytId);
									}}
								/>
							) : (
								<iframe
									title="Video della partita"
									className="mvt__player"
									src={`https://player.vimeo.com/video/${vmId}#t=${videoStartTime}s`}
									width="100%"
									frameBorder="0"
									allow="autoplay; fullscreen; picture-in-picture"
									allowFullScreen
								/>
							)}
						</div>

						<div className="mvt__panel">
							{TAG_GROUPS.map((group) => (
								<div key={group}>
									<div className="mvt__group">{group}</div>
									<div className="mvt__tags">
										{TAG_TYPES.filter((t) => t.group === group).map((t) => (
											<button
												key={t.value}
												type="button"
												className={
													"mvt-tag" +
													(pendingTag?.tagType === t.value
														? " mvt-tag--active"
														: "")
												}
												style={{"--tag-color": tagColor(t.value)}}
												onClick={() => startPendingTag(t.value)}
											>
												<span className="mvt-dot" />
												{t.label}
											</button>
										))}
									</div>
								</div>
							))}

							{pendingTag && (
								<div
									className="mvt-form"
									style={{"--tag-color": tagColor(pendingTag.tagType)}}
								>
									<div className="mvt-form__head">
										<span className="mvt-dot" />
										<span className="mvt-form__label">
											{pendingTag.tagLabel}
										</span>
										<span className="mvt-form__time">
											{fmtTime(pendingTag.minute, pendingTag.second)}
										</span>
									</div>
									<div className="mvt-form__grid">
										<div>
											<VLFTextField
												label="Minuto"
												type="number"
												margin="none"
												value={pendingTag.minute}
												onChange={(e) =>
													updatePending("minute", e.target.value)
												}
												inputProps={{min: 0, max: 120}}
											/>
										</div>
										<div>
											<VLFTextField
												label="Secondo"
												type="number"
												margin="none"
												value={pendingTag.second}
												onChange={(e) =>
													updatePending("second", e.target.value)
												}
												inputProps={{min: 0, max: 59}}
											/>
										</div>
										<div className="mvt-form__wide">
											<VLFSelectNew
												label="Squadra"
												options={teamOptions}
												sorted={false}
												noMargin
												placeholder="Entrambe"
												value={
													teamOptions.find(
														(o) => o.value === pendingTag.team,
													) || null
												}
												onChange={(option) =>
													updatePending("team", option?.value || "")
												}
											/>
										</div>
										<div>
											<VLFTextField
												label="Giocatore"
												placeholder="Facoltativo"
												margin="none"
												value={pendingTag.playerName}
												onChange={(e) =>
													updatePending("playerName", e.target.value)
												}
											/>
										</div>
										<div>
											<VLFTextField
												label="Note"
												placeholder="Facoltative"
												margin="none"
												value={pendingTag.notes}
												onChange={(e) => updatePending("notes", e.target.value)}
											/>
										</div>
									</div>
									<MainActions
										actions={[
											{
												label: "Annulla",
												variant: "outlined",
												onClick: () => setPendingTag(null),
											},
											{label: "Aggiungi", onClick: confirmPendingTag},
										]}
									/>
								</div>
							)}
						</div>
					</div>

					{videoTags.length > 0 ? (
						<div className="mvt__columns">
							{renderColumn(matchData?.homeClub || "Casa", homeTags, "h")}
							{renderColumn(matchData?.awayClub || "Ospite", awayTags, "a")}
							{neutralTags.length > 0 &&
								renderColumn("Entrambe le squadre", neutralTags, "n")}
						</div>
					) : (
						<p className="mvt-empty">
							Tocca un'azione per segnare un momento della partita.
						</p>
					)}
				</div>
			)}
		</div>
	);
}
